import { ServiceResponse } from "@rsm-hcd/javascript-core";
import { NotebookShareScope } from "models/enumerations/notebooks/notebook-share-scope";
import { PermissionScope } from "models/enumerations/notebooks/permission-scope";
import NotebookPermissionManifestRecord from "models/view-models/notebooks/notebook-permission-manifest-record";
import NotebookResourcePermissionRecord from "models/view-models/notebooks/notebook-resource-permission-record";
import PageRecord from "models/view-models/page-record";
import { NotebookPermissionSelectOption } from "pages/notebooks/use-notebook-sharing-dropdown-options";
import { useState } from "react";
import { CollectionUtils } from "utilities/collection-utils";
import useNotebookPermissionsApi from "utilities/hooks/domain/notebooks/use-notebook-permissions-api";
import useDebounce from "utilities/hooks/use-debounce";
import { t } from "utilities/localization-utils";
import {
    NotebookPermissionPostParams,
    NotebookPermissionPutParams,
} from "utilities/services/notebooks/notebook-permission-service";
import { ToastManager } from "utilities/toast/toast-manager";

const useNotebookShareModal = ({ closeDialog, notebook }) => {
    const [recipientsSearchText, setRecipientsSearchText] =
        useState<string>("");
    const debouncedSearchText = useDebounce(recipientsSearchText, 300);

    const {
        createNotebookPermissionApi,
        updateNotebokPermissionApi,
        deleteNotebookPermissionsApi,
        availableRecipientsResult,
        notebookPermissions,
        isLoadingPermissions,
        isLoadingRecipients,
    } = useNotebookPermissionsApi({
        notebook,
        recipientsSearchText: debouncedSearchText,
    });

    // #region State
    const [draftRecipientSaves, setDraftRecipientSaves] = useState<
        NotebookPermissionManifestRecord[]
    >([]);
    const [draftSharedPages, setDraftSharedPages] = useState<PageRecord[]>([]);
    const [draftScope, setDraftScope] = useState<NotebookShareScope>();
    const [draftPermissionRemovals, setDraftPermissionRemovals] = useState<
        number[]
    >([]);

    const [isLoading, setIsLoading] = useState(false);
    // #endregion State

    // #region Computed Values

    // Current share scope of the notebook. Defaults to "Entire Notebook"
    const persistedScope =
        notebookPermissions?.resultObjects[0]?.permission?.shareScope ??
        NotebookShareScope.EntireNotebook;

    // List of PageRecords that are currently saved as shared
    const persistedSharedPages =
        notebook.pages?.filter((page) =>
            notebookPermissions?.resultObjects[0]?.permission?.notebookPermissionPages?.some(
                (p) => p.pageId === page.id
            )
        ) ?? [];

    // List of PageRecords that are currently shared - either the draft list or the saved list if no changes
    const currentlySharedPages: PageRecord[] = CollectionUtils.hasValues(
        draftSharedPages
    )
        ? draftSharedPages
        : (persistedSharedPages ?? []);

    // Persisted permissions minus removals (so that persisted removals can be re-added)
    const persistedNotebookPermissionsMinusRemovals =
        notebookPermissions?.resultObjects?.filter(
            (manifest) =>
                !draftPermissionRemovals.includes(manifest.permission.id!)
        ) ?? [];

    // Controls if the Page Access collapse panel is available
    const canShowPageAccessArea =
        CollectionUtils.hasValues(draftRecipientSaves) ||
        CollectionUtils.hasValues(notebookPermissions?.resultObjects);

    // Determines the current share scope of the notebook (entire notebook or selected pages)
    const currentNotebookShareScope = draftScope ? draftScope : persistedScope;

    // Available recipients
    const availableRecipients = availableRecipientsResult?.resultObjects ?? [];

    // List of page ids that need to be added when creating or updating permissions
    const pagesToAdd: number[] =
        currentNotebookShareScope === NotebookShareScope.SelectPages
            ? (currentlySharedPages.map((p) => p.id!) ?? [])
            : [];

    // #endregion Computed Values

    // #region Helper Methods
    const handleOnRemovePermissions = (
        permissionManifest: NotebookPermissionManifestRecord
    ) => {
        setDraftRecipientSaves((prevSaves) => [
            ...prevSaves.filter(
                (save) =>
                    !(
                        save.recipient?.id ===
                            permissionManifest.recipient?.id &&
                        permissionManifest.permission?.recipientType ===
                            save.recipient?.type
                    )
            ),
        ]);
    };

    const handleOnRemovePersistedPermission = (
        permissionManifest: NotebookPermissionManifestRecord
    ) => {
        const idToRemove = permissionManifest.permission.id;
        if (idToRemove !== undefined) {
            setDraftPermissionRemovals((prevRemovals) => [
                ...prevRemovals,
                idToRemove,
            ]);
        }
    };

    const buildDeleteActions = (): Array<Promise<ServiceResponse<Boolean>>> => {
        return draftPermissionRemovals.length
            ? [
                  deleteNotebookPermissionsApi(
                      { notebookId: notebook.id! },
                      { permissionIds: draftPermissionRemovals }
                  ),
              ]
            : [];
    };

    const buildUpdateCalls = () => {
        const isPageListUpdated = !CollectionUtils.equalsBy<
            PageRecord,
            unknown
        >((e) => e.id, currentlySharedPages, persistedSharedPages);
        if (
            (draftScope && draftScope !== persistedScope) ||
            isPageListUpdated
        ) {
            const existingPermissionsToUpdate =
                notebookPermissions?.resultObjects
                    .filter((manifest) =>
                        isPageListUpdated
                            ? true // If the page list was updated, we have to update all permissions. Otherise, update only changed permissions
                            : manifest.permission.shareScope !==
                              currentNotebookShareScope
                    )
                    .map((manifest) => {
                        return new NotebookResourcePermissionRecord().with({
                            ...manifest.permission.toObject(),
                            shareScope: currentNotebookShareScope,
                        });
                    })
                    .filter((x) => x !== undefined);
            if (existingPermissionsToUpdate) {
                return existingPermissionsToUpdate.map((permission) => {
                    const putParams: NotebookPermissionPutParams = {
                        permission: permission.permission,
                        shareScope: permission.shareScope,
                        resourceType: permission.resourceType,
                        pageIds: pagesToAdd,
                    };
                    return updateNotebokPermissionApi(putParams, {
                        notebookId: notebook.id!,
                        permissionId: permission.id!,
                    });
                });
            }
        }
        return [];
    };

    const buildCreateActions = () => {
        const newPermissions: NotebookPermissionPostParams[] =
            draftRecipientSaves
                .filter((save) => !save.permission.id)
                .map((save) =>
                    save.with({
                        permission: save.permission.with({
                            shareScope: currentNotebookShareScope,
                        }),
                    })
                )
                .map((save) => {
                    return {
                        ...save.permission.toObject(),
                        pageIds: pagesToAdd,
                    };
                });

        return newPermissions.map((permission) =>
            createNotebookPermissionApi(
                {
                    notebookId: notebook.id!,
                },
                permission
            )
        );
    };

    // #endregion Helper Methods

    // #region Handlers
    const onAddNewPermission = (newVals: NotebookPermissionSelectOption[]) => {
        const valuesFromUpdatedSelectedOptions = newVals
            .map((val) => val.data)
            .filter(
                (data): data is NotebookPermissionManifestRecord => data != null
            );

        setDraftRecipientSaves((prevSaves) => {
            const newRecipientSaves = valuesFromUpdatedSelectedOptions.filter(
                (newSave) =>
                    !prevSaves.some(
                        (existingSave) =>
                            existingSave.recipient?.id ===
                                newSave.recipient?.id &&
                            existingSave.recipient?.type ===
                                newSave.recipient?.type
                    )
            );

            return [...prevSaves, ...newRecipientSaves];
        });
    };

    const onUpdateExistingPermission = (
        permission: NotebookResourcePermissionRecord,
        newPermissionScope: PermissionScope
    ) => {
        setDraftRecipientSaves((prevSaves) => [
            ...prevSaves.map((save) =>
                save.recipient?.id === permission.recipientId &&
                permission.recipientType === save.recipient?.type
                    ? save.withPermission({ permission: newPermissionScope })
                    : save
            ),
        ]);
    };

    const onRemovePermission = (
        permissionManifest: NotebookPermissionManifestRecord
    ) => {
        permissionManifest.permission.id
            ? handleOnRemovePersistedPermission(permissionManifest)
            : handleOnRemovePermissions(permissionManifest);
    };

    const onSave = async () => {
        try {
            setIsLoading(true);

            const createApiCalls = buildCreateActions();
            const deleteApiCalls = buildDeleteActions();
            const updateApiCalls = buildUpdateCalls();

            const responses = await Promise.all([
                ...createApiCalls,
                ...updateApiCalls,
                ...deleteApiCalls,
            ]);

            const errors: string[] = [];
            responses.forEach((response) => {
                if (response.result?.errors) {
                    errors.push(response.result.errors?.join(", "));
                }
            });
            if (CollectionUtils.hasValues(errors)) {
                throw errors.join(", ");
            }
            ToastManager.success(
                t("success-actionResource", {
                    action: t("shared"),
                    resource: t("notebook"),
                })
            );
            closeDialog();
        } catch (error) {
            ToastManager.error(
                t("errors-actionResource", {
                    action: t("sharing").toLowerCase(),
                    resource: t("notebook"),
                })
            );
        } finally {
            setIsLoading(false);
        }
    };
    // #endregion Helper Methods

    return {
        availableRecipients,
        canShowPageAccessArea,
        currentlySharedPages,
        currentNotebookShareScope,
        draftRecipientSaves,
        isLoading: isLoading && isLoadingRecipients,
        isLoadingPermissions,
        onAddNewPermission,
        onRemovePermission,
        onSave,
        onUpdateExistingPermission,
        persistedNotebookPermissionsMinusRemovals,
        setDraftSharedPages,
        setDraftScope,
        setRecipientsSearchText,
        isLoadingRecipients:
            // compare with debounced text to show the loading state while the user is typing before the request is started
            isLoadingRecipients || recipientsSearchText !== debouncedSearchText,
    };
};

export default useNotebookShareModal;
